package com.sweetlime.dungeonamigo.presentation.features.dice

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.dungeonamigo.model.DiceRoll
import com.sweetlime.dungeonamigo.model.DiceType
import com.sweetlime.dungeonamigo.model.SingleRoll
import com.sweetlime.dungeonamigo.presentation.ui.components.DiceCanvas
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.presentation.ui.theme.pixelFont
import com.sweetlime.dungeonamigo.presentation.ui.theme.silkFont

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DiceRollerSheet(
    vm: DiceViewModel,
    label: String = "",
    onDismiss: () -> Unit,
) {
    val pf = pixelFont()
    val sf = silkFont()
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val selectedDice by vm.selectedDice.collectAsState()
    val modifier by vm.modifier.collectAsState()
    val advantage by vm.advantage.collectAsState()
    val lastRoll by vm.lastRoll.collectAsState()
    val rollHistory by vm.rollHistory.collectAsState()
    val isAnimating by vm.isAnimating.collectAsState()
    val displayDice by vm.displayDice.collectAsState()

    val rollLabel = when {
        isAnimating -> "Rolling…"
        selectedDice.size == 1 -> {
            val (type, count) = selectedDice.entries.first()
            "Roll ${if (count == 1) type.label else "${count}${type.label}"}"
        }
        else -> "Roll"
    }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
        shape = RoundedCornerShape(topStart = 22.dp, topEnd = 22.dp),
        containerColor = BrandColors.Stone,
        dragHandle = {
            Box(
                Modifier
                    .padding(vertical = 8.dp)
                    .size(32.dp, 4.dp)
                    .clip(CircleShape)
                    .background(BrandColors.Slate),
            )
        },
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 18.dp)
                .padding(bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp),
        ) {
            // Title
            Text(
                text = if (label.isNotBlank()) label else "Dice Roller",
                color = BrandColors.Ivory,
                fontFamily = pf,
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
            )

            // ── Section 1: Die type grid ──────────────────────────────────────
            DieTypeGrid(
                selectedDice = selectedDice,
                isAnimating = isAnimating,
                onAddDie = vm::addDie,
                onRemoveDie = vm::removeDie,
                onClear = vm::clearDice,
            )

            // ── Section 2: Selected dice display ─────────────────────────────
            val dicesToShow: List<SingleRoll> = when {
                isAnimating || displayDice.isNotEmpty() -> displayDice
                selectedDice.isNotEmpty() ->
                    selectedDice.entries.flatMap { (type, count) ->
                        List(count) { SingleRoll(type, 0) }
                    }
                else -> emptyList()
            }

            AnimatedVisibility(visible = dicesToShow.isNotEmpty()) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(
                        "SELECTED",
                        fontFamily = sf,
                        fontSize = 8.sp,
                        color = BrandColors.IvoryDim,
                        letterSpacing = 1.sp,
                    )
                    LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        items(dicesToShow) { roll ->
                            DiceCanvas(
                                diceType = roll.diceType,
                                value = roll.result,
                                isAnimating = isAnimating,
                                isSelected = true,
                                isDropped = roll.isDropped,
                                size = 72.dp,
                            )
                        }
                    }
                }
            }

            // ── Section 3: Modifier stepper ───────────────────────────────────
            ModifierStepper(modifier = modifier, onModify = vm::setModifier)

            // ── Section 4: Advantage / Disadvantage ───────────────────────────
            val canAdvantage = selectedDice[DiceType.D20] == 1
            AdvantageToggle(
                state = advantage,
                enabled = canAdvantage,
                onSet = vm::setAdvantage,
            )

            // ── Section 5: Roll button ─────────────────────────────────────────
            Button(
                onClick = { vm.roll(label) },
                modifier = Modifier.fillMaxWidth().height(52.dp),
                colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
                shape = RoundedCornerShape(12.dp),
                enabled = selectedDice.isNotEmpty() && !isAnimating,
            ) {
                if (isAnimating) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        CircularProgressIndicator(
                            Modifier.size(18.dp),
                            color = BrandColors.Ivory,
                            strokeWidth = 2.dp,
                        )
                        Text(
                            "Rolling…",
                            fontFamily = pf,
                            fontSize = 16.sp,
                            fontWeight = FontWeight.Bold,
                            color = BrandColors.Ivory,
                        )
                    }
                } else {
                    Text(
                        rollLabel,
                        fontFamily = pf,
                        fontSize = 18.sp,
                        fontWeight = FontWeight.Bold,
                        color = BrandColors.Ivory,
                    )
                }
            }

            // ── Section 6: Result display ──────────────────────────────────────
            AnimatedVisibility(
                visible = lastRoll != null && !isAnimating,
                enter = fadeIn(),
                exit = fadeOut(),
            ) {
                lastRoll?.let { roll -> RollResultDisplay(roll) }
            }

            // ── Section 7: Roll history ────────────────────────────────────────
            if (rollHistory.isNotEmpty()) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(
                        "HISTORY",
                        fontFamily = sf,
                        fontSize = 8.sp,
                        color = BrandColors.IvoryDim,
                        letterSpacing = 1.sp,
                    )
                    LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        items(rollHistory.take(4)) { roll ->
                            HistoryChip(roll = roll, onClick = { vm.rerollFromHistory(roll) })
                        }
                    }
                }
            }
        }
    }
}

// ── Die Type Grid ─────────────────────────────────────────────────────────────

@Composable
private fun DieTypeGrid(
    selectedDice: Map<DiceType, Int>,
    isAnimating: Boolean,
    onAddDie: (DiceType) -> Unit,
    onRemoveDie: (DiceType) -> Unit,
    onClear: () -> Unit,
) {
    val pf = pixelFont()
    val sf = silkFont()

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                "DICE",
                fontFamily = sf,
                fontSize = 8.sp,
                color = BrandColors.IvoryDim,
                letterSpacing = 1.sp,
            )
            if (selectedDice.isNotEmpty()) {
                TextButton(
                    onClick = onClear,
                    enabled = !isAnimating,
                    contentPadding = PaddingValues(horizontal = 8.dp, vertical = 0.dp),
                ) {
                    Text("Clear", color = BrandColors.Ember, fontSize = 12.sp)
                }
            }
        }

        DiceType.entries.chunked(4).forEach { row ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                row.forEach { type ->
                    val count = selectedDice[type] ?: 0
                    val isActive = count > 0
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .height(38.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .background(
                                if (isActive) BrandColors.Ember.copy(alpha = 0.13f) else BrandColors.Vault,
                            )
                            .border(
                                1.dp,
                                if (isActive) BrandColors.Ember else BrandColors.Slate.copy(alpha = 0.4f),
                                RoundedCornerShape(8.dp),
                            )
                            .combinedClickable(
                                onClick = { if (!isAnimating) onAddDie(type) },
                                onLongClick = { if (count > 0 && !isAnimating) onRemoveDie(type) },
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text = if (count > 1) "${count}${type.label}" else type.label,
                            fontFamily = pf,
                            fontSize = 14.sp,
                            fontWeight = if (isActive) FontWeight.Bold else FontWeight.Normal,
                            color = if (isActive) BrandColors.Ivory else BrandColors.IvoryDim,
                        )
                    }
                }
                // Pad last row to keep grid aligned
                repeat(4 - row.size) {
                    Spacer(Modifier.weight(1f))
                }
            }
        }
    }
}

// ── Modifier Stepper ──────────────────────────────────────────────────────────

@Composable
private fun ModifierStepper(modifier: Int, onModify: (Int) -> Unit) {
    val pf = pixelFont()
    val sf = silkFont()

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            "MODIFIER",
            fontFamily = sf,
            fontSize = 8.sp,
            color = BrandColors.IvoryDim,
            letterSpacing = 1.sp,
        )
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            StepButton("−") { onModify(modifier - 1) }
            Text(
                text = when {
                    modifier > 0 -> "+$modifier"
                    modifier < 0 -> "$modifier"
                    else -> "0"
                },
                fontFamily = pf,
                fontSize = 22.sp,
                fontWeight = FontWeight.Bold,
                color = BrandColors.Ivory,
                modifier = Modifier.widthIn(min = 38.dp),
            )
            StepButton("+") { onModify(modifier + 1) }
        }
    }
}

@Composable
private fun StepButton(label: String, onClick: () -> Unit) {
    val pf = pixelFont()
    Box(
        modifier = Modifier
            .size(38.dp)
            .clip(RoundedCornerShape(9.dp))
            .background(BrandColors.StoneLight)
            .border(1.dp, BrandColors.Slate.copy(alpha = 0.47f), RoundedCornerShape(9.dp))
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            label,
            fontFamily = pf,
            fontSize = 22.sp,
            fontWeight = FontWeight.Bold,
            color = BrandColors.Ivory,
        )
    }
}

// ── Advantage Toggle ──────────────────────────────────────────────────────────

@Composable
private fun AdvantageToggle(
    state: AdvantageState,
    enabled: Boolean,
    onSet: (AdvantageState) -> Unit,
) {
    val sf = silkFont()
    val pf = pixelFont()

    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            "ADVANTAGE",
            fontFamily = sf,
            fontSize = 8.sp,
            color = if (enabled) BrandColors.IvoryDim else BrandColors.Slate,
            letterSpacing = 1.sp,
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            listOf(
                Triple("Normal", AdvantageState.NORMAL, BrandColors.StoneLight),
                Triple("Advantage", AdvantageState.ADVANTAGE, BrandColors.Parchment),
                Triple("Disadvantage", AdvantageState.DISADVANTAGE, BrandColors.Ember),
            ).forEach { (label, advState, activeColor) ->
                val isSelected = state == advState
                val alpha = if (enabled) 1f else 0.4f
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .clip(RoundedCornerShape(8.dp))
                        .background(
                            if (isSelected) activeColor.copy(alpha = alpha)
                            else BrandColors.StoneLight.copy(alpha = alpha),
                        )
                        .clickable(enabled = enabled) { onSet(advState) }
                        .padding(vertical = 10.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = label,
                        fontFamily = pf,
                        fontSize = 12.sp,
                        fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                        color = if (isSelected && activeColor != BrandColors.StoneLight)
                            BrandColors.Vault
                        else
                            BrandColors.Ivory.copy(alpha = alpha),
                    )
                }
            }
        }
    }
}

// ── Roll Result Display ───────────────────────────────────────────────────────

@Composable
private fun RollResultDisplay(roll: DiceRoll) {
    val pf = pixelFont()
    val sf = silkFont()

    val ringColor = when {
        roll.isCrit -> BrandColors.Parchment
        roll.isCritFail -> BrandColors.EmberDim
        else -> BrandColors.Ember
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(BrandColors.StoneLight)
            .border(2.dp, ringColor, RoundedCornerShape(14.dp))
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        if (roll.label.isNotBlank()) {
            Text(roll.label, color = BrandColors.IvoryDim, fontSize = 13.sp)
        }

        if (roll.isCrit || roll.isCritFail) {
            Text(
                text = if (roll.isCrit) "CRITICAL!" else "FUMBLE!",
                fontFamily = sf,
                fontSize = 12.sp,
                letterSpacing = 2.sp,
                color = ringColor,
            )
        }

        Text(
            text = "${roll.total}",
            color = ringColor,
            fontFamily = pf,
            fontSize = 56.sp,
            fontWeight = FontWeight.ExtraBold,
        )

        LazyRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp, Alignment.CenterHorizontally),
            modifier = Modifier.fillMaxWidth(),
        ) {
            items(roll.dice) { singleRoll ->
                DiceCanvas(
                    diceType = singleRoll.diceType,
                    value = singleRoll.result,
                    isAnimating = false,
                    isSelected = !singleRoll.isDropped,
                    isDropped = singleRoll.isDropped,
                    size = 52.dp,
                )
            }
        }

        if (roll.modifier != 0) {
            Text(
                text = "mod ${if (roll.modifier > 0) "+" else ""}${roll.modifier}",
                fontFamily = sf,
                fontSize = 8.sp,
                letterSpacing = 1.sp,
                color = BrandColors.IvoryDim,
            )
        }

        if (roll.isAdvantage) {
            Text(
                "ADVANTAGE",
                fontFamily = sf,
                fontSize = 8.sp,
                letterSpacing = 1.sp,
                color = BrandColors.Parchment,
            )
        } else if (roll.isDisadvantage) {
            Text(
                "DISADVANTAGE",
                fontFamily = sf,
                fontSize = 8.sp,
                letterSpacing = 1.sp,
                color = BrandColors.Ember,
            )
        }
    }
}

// ── History Chip ──────────────────────────────────────────────────────────────

@Composable
private fun HistoryChip(roll: DiceRoll, onClick: () -> Unit) {
    val pf = pixelFont()
    val sf = silkFont()

    val dieLabel = buildString {
        val groups = roll.dice.filter { !it.isDropped }.groupBy { it.diceType }
        groups.entries.forEachIndexed { i, (type, rolls) ->
            if (i > 0) append("+")
            if (rolls.size > 1) append(rolls.size)
            append(type.label)
        }
        if (roll.modifier != 0) append(if (roll.modifier > 0) "+${roll.modifier}" else "${roll.modifier}")
    }

    Box(
        modifier = Modifier
            .widthIn(min = 44.dp)
            .clip(RoundedCornerShape(7.dp))
            .background(BrandColors.Vault)
            .border(1.dp, BrandColors.Slate.copy(alpha = 0.33f), RoundedCornerShape(7.dp))
            .clickable(onClick = onClick)
            .padding(horizontal = 10.dp, vertical = 6.dp),
        contentAlignment = Alignment.Center,
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text(
                text = "${roll.total}",
                fontFamily = pf,
                fontSize = 15.sp,
                fontWeight = FontWeight.Bold,
                color = BrandColors.Ivory,
            )
            Text(
                text = dieLabel,
                fontFamily = sf,
                fontSize = 7.sp,
                color = BrandColors.IvoryDim,
                letterSpacing = 0.5.sp,
            )
        }
    }
}
